import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { Icon } from './ui/Icon.jsx';
import T from '../locales/translations.js';
import { db, supaFetch } from '../lib/supabase.js';
import { fmt } from '../lib/utils.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import {
  useOrders,
  useUpdateOrderStatus,
  useDeleteOrder,
  useSaveAdminNote,
} from '../hooks/useOrders.js';
import { useOrderRecipients, useSetFulfillmentContact } from '../hooks/useOrderRecipients.js';
import { openInvoiceSheet } from '../lib/invoiceSheet.js';

// ─── LOCAL CONSTANTS ──────────────────────────────────────────────────────────
const STATUS_STAGES = ['new', 'confirmed', 'processing', 'shipped', 'delivered'];

const STATUS_LABELS = {
  new:        { en: 'New',        ar: 'جديد',           icon: 'Download',    color: 'var(--status-danger)', bg: 'var(--status-danger-bg)' },
  confirmed:  { en: 'Confirmed',  ar: 'مؤكد',           icon: 'CheckCircle2', color: 'var(--status-info)', bg: '#dbeafe' },
  processing: { en: 'Processing', ar: 'قيد التجهيز',    icon: 'Settings',     color: 'var(--status-warn)', bg: 'var(--status-warn-bg)' },
  shipped:    { en: 'Shipped',    ar: 'جاري التوصيل',   icon: 'Truck',        color: '#7c3aed', bg: '#ede9fe' },
  delivered:  { en: 'Delivered',  ar: 'تم التسليم',     icon: 'Package',      color: 'var(--status-success)', bg: 'var(--status-success-bg)' },
  cancelled:  { en: 'Cancelled',  ar: 'ملغي',           icon: 'Ban',          color: '#6b7280', bg: '#f3f4f6' },
};

const PAYMENT_LABELS = {
  instapay: { label: 'Instapay',       icon: 'CreditCard' },
  bank:     { label: 'Bank Transfer',  icon: 'Landmark' },
  cheque:   { label: 'Cheque',         icon: 'FileText' },
  cash:     { label: 'Cash',           icon: 'Banknote' },
};

const LIMIT = 50;

// ─── ORDERS COMPONENT ────────────────────────────────────────────────────────
export default function Orders({ lang, canSeeTotals, canStatus = true, isSuperAdmin = false }) {
  const t        = T[lang];
  const isMobile = useIsMobile();

  // ── Server-side filter state (drives the query key) ──────────────────────
  const [page,         setPage]         = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Client-side filter state (applied on top of the current page) ────────
  const [orderSearch, setOrderSearch] = useState('');
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');

  // ── Fulfillment comms: who each order is actually contacted through ───────
  const { recipientFor, contactsForAccount } = useOrderRecipients();
  const setFulfillment = useSetFulfillmentContact();

  // ── UI-only state ─────────────────────────────────────────────────────────
  const [xlsxLoading,    setXlsxLoading]    = useState(null);
  const [sheetLoading,   setSheetLoading]   = useState(null); // printable-sheet export in flight
  const [adminNoteEdit,  setAdminNoteEdit]  = useState({}); // orderId → draft
  // ── Bill merging ──────────────────────────────────────────────────────────
  const [selectedForMerge, setSelectedForMerge] = useState(new Set());
  const [mergeLoading,     setMergeLoading]      = useState(false);
  const [mergeMsg,         setMergeMsg]          = useState('');

  // ── TanStack Query ────────────────────────────────────────────────────────
  const { data, isLoading, isError, isFetching } = useOrders({
    page,
    limit: LIMIT,
    status: statusFilter,
  });

  const orders     = data?.data       || [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / LIMIT));

  // Mutations
  const { mutate: updateStatusMutation } = useUpdateOrderStatus();
  const { mutate: deleteOrder }   = useDeleteOrder();
  const { mutate: saveNote, isPending: savingNote } = useSaveAdminNote();

  // ── Overflow safety: if server shrinks and page is now out of range ───────
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ── Reset to page 1 when status filter changes ────────────────────────────
  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const updateStatus = ({ orderId, newStatus }) => {
    updateStatusMutation(
      { orderId, newStatus },
      {
        onSuccess: () => posthog.capture('order_status_updated', {
          order_id: orderId,
          new_status: newStatus,
        }),
      }
    );
  };

  const btnStyle = (c = 'var(--sedra-olive)') => ({
    padding: '6px 12px', borderRadius: 6, background: c, color: '#fff',
    border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
  });

  // ── Client-side search / date filter applied on top of current page ───────
  const filteredOrders = orders.filter((o) => {
    const oStatus = o.status || 'new';
    // Status filter is handled server-side; 'new' also covers 'seen' in Supabase query
    if (dateFrom && (o.created_at || '') < dateFrom) return false;
    if (dateTo   && (o.created_at || '') > dateTo + 'T23:59:59') return false;
    if (orderSearch) {
      const q   = orderSearch.toLowerCase();
      const hay = [o.id, o.business_name, o.contact_name, o.phone, o.email, o.address]
        .join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // ── Print ─────────────────────────────────────────────────────────────────
  // ── Printable invoice, optionally WITH product pictures ───────────────────
  // Shares one sheet with the customer-facing exports (src/lib/invoiceSheet.js).
  // Orders don't store images — placeOrder snapshots only id/sku/name/qty/price
  // — so covers are resolved by product id at export time. invoiceSheet
  // compresses each one to an embedded thumbnail, keeping the PDF small enough
  // to email or send on WhatsApp.
  const printOrder = async (o, { withPictures = false } = {}) => {
    if (sheetLoading) return;
    setSheetLoading(o.id + (withPictures ? ':pic' : ''));
    try {
      const items = Array.isArray(o.items) ? o.items : [];
      let imgById = {};
      if (withPictures) {
        const ids = [...new Set(items.map(i => i.id).filter(Boolean))];
        if (ids.length) {
          const list = ids.map(id => `"${String(id).replace(/"/g, '')}"`).join(',');
          const { data } = await supaFetch('GET', 'products', null, `?id=in.(${list})&select=id,image`);
          (data || []).forEach(p => { imgById[p.id] = p.image; });
        }
      }
      const r = recipientFor(o);
      const ok = await openInvoiceSheet({
        withPictures,
        lang,
        kind:   'invoice',
        refId:  o.id,
        dateMs: o.created_at ? new Date(o.created_at).getTime() : undefined,
        party: {
          company_name: o.business_name || o.contact_name,
          contact_name: r.name  || o.contact_name,
          phone:        r.phone || o.phone,
        },
        items: items.map(i => ({ ...i, image: i.image || imgById[i.id] || '' })),
        total:     o.total,
        showPrice: canSeeTotals,
        showVat:   o.catalogue_type === 'vat',
        notes:     [o.notes, o.admin_notes].filter(Boolean).join(' — '),
      });
      if (!ok) alert(lang === 'ar' ? 'يرجى السماح بالنوافذ المنبثقة' : 'Please allow popups for this site');
    } catch (e) {
      alert((lang === 'ar' ? 'حدث خطأ: ' : 'Error: ') + e.message);
    }
    setSheetLoading(null);
  };

  // ── CSV export (current page — full export is a future feature) ───────────
  const exportCSV = () => {
    const rows = [['Order ID', 'Date', 'Business', 'Contact', 'Phone', 'WhatsApp', 'Email', 'Address', 'Type', 'Payment', 'Status', 'Items', 'Total', 'Notes']];
    orders.forEach((o) => {
      const items = Array.isArray(o.items)
        ? o.items.map((i) => `${i.sku}x${i.qty}`).join('; ')
        : '';
      rows.push([
        o.id, (o.created_at || '').slice(0, 10), o.business_name, o.contact_name,
        o.phone, o.whatsapp || '', o.email || '', o.address || '',
        o.catalogue_type, o.payment_method || '', o.status || 'new',
        items, o.total, o.notes || '',
      ]);
    });
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const a = document.createElement('a');
    a.href     = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csv);
    a.download = `sedra_orders_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // ── Invoice (SheetJS, lazy-loaded) ────────────────────────────────────────
  const loadXLSX = () =>
    new Promise((res, rej) => {
      if (window.XLSX) { res(window.XLSX); return; }
      const s = document.createElement('script');
      s.src    = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
      s.onload = () => res(window.XLSX);
      s.onerror = rej;
      document.head.appendChild(s);
    });

  const exportInvoice = async (o) => {
    setXlsxLoading(o.id);
    try {
      const XLSX    = await loadXLSX();
      const items   = Array.isArray(o.items) ? o.items : [];
      const showVat = o.catalogue_type === 'vat';
      const [cn, ca, cp, ce] = await Promise.all([
        db.getSetting('company_name'),
        db.getSetting('company_address'),
        db.getSetting('company_phone'),
        db.getSetting('company_email'),
      ]);
      const coName    = cn || 'Sedra Home';
      const coAddress = ca || '';
      const coPhone   = cp || '';
      const coEmail   = ce || 'sedrahome.com';

      const xlRows = [];
      xlRows.push([coName + (coName !== 'Sedra Home' ? ' — سدرة هوم' : ' — سدرة هوم'), '', '', '', '', '']);
      xlRows.push([[coAddress, coPhone, coEmail].filter(Boolean).join('  |  ') || 'sedrahome.com', '', '', '', '', '']);
      xlRows.push([]);
      xlRows.push(['INVOICE / فاتورة', '', '', '', '', '']);
      xlRows.push([]);
      xlRows.push(['Order ID / رقم الطلب', o.id,             '', 'Date / التاريخ',  (o.created_at || '').slice(0, 10), '']);
      xlRows.push(['Type / النوع',         o.catalogue_type, '', 'Status / الحالة', (STATUS_LABELS[o.status] || {}).en || o.status, '']);
      xlRows.push([]);
      xlRows.push(['BILL TO / بيانات العميل', '', '', '', '', '']);
      xlRows.push(['Business / المنشأة',  o.business_name || '—', '', '', '', '']);
      xlRows.push(['Contact / المسؤول',   o.contact_name  || '—', '', '', '', '']);
      xlRows.push(['Phone / الهاتف',      o.phone         || '—', '', '', '', '']);
      if (o.whatsapp) xlRows.push(['WhatsApp', o.whatsapp, '', '', '', '']);
      if (o.email)    xlRows.push(['Email / البريد', o.email, '', '', '', '']);
      if (o.address)  xlRows.push(['Address / العنوان', o.address, '', '', '', '']);
      xlRows.push([]);
      xlRows.push(['#', 'SKU', 'Product / المنتج', 'Qty',
        showVat ? 'Unit Price (incl.VAT)' : 'Unit Price / سعر الوحدة',
        'Subtotal / المجموع']);
      items.forEach((item, idx) => {
        xlRows.push([
          idx + 1, item.sku,
          lang === 'ar' && item.name_ar ? item.name_ar : (item.name_en || item.name_ar),
          item.qty, item.price, item.price * item.qty,
        ]);
      });
      xlRows.push([]);
      xlRows.push(['', '', '', '', showVat ? 'TOTAL (incl. 14% VAT)' : 'TOTAL / الإجمالي', o.total]);
      if (showVat) xlRows.push(['', '', '', '', 'VAT amount (14%)', +(o.total - o.total / 1.14).toFixed(2)]);
      xlRows.push([]);
      if (o.notes) xlRows.push(['Notes / ملاحظات', o.notes, '', '', '', '']);
      xlRows.push([]);
      xlRows.push(['Thank you for your business! — شكراً لتعاملكم معنا 🌿', '', '', '', '', '']);

      const ws = XLSX.utils.aoa_to_sheet(xlRows);
      ws['!cols'] = [{ wch: 28 }, { wch: 30 }, { wch: 34 }, { wch: 10 }, { wch: 26 }, { wch: 18 }];
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
        { s: { r: xlRows.length - 1, c: 0 }, e: { r: xlRows.length - 1, c: 5 } },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Invoice');
      XLSX.writeFile(wb, `Sedra_Invoice_${o.id}.xlsx`);
    } catch (e) { alert('Could not generate invoice: ' + e.message); }
    setXlsxLoading(null);
  };

  // ── Email / WhatsApp ──────────────────────────────────────────────────────
  // Addressed to the order's CURRENT fulfillment recipient — the pinned
  // fulfillment contact, else the account's active primary — not the frozen
  // placed-by snapshot, which may name someone who has left the company.
  const sendEmail = (o) => {
    const r = recipientFor(o);
    if (!r.email) return alert('No email address for this order.');
    const items   = (o.items || []).map((i) =>
      `  • ${lang === 'ar' && i.name_ar ? i.name_ar : i.name_en} (${i.sku})  x${i.qty}  →  ${fmt(i.price * i.qty)}`
    ).join('\n');
    const subject = `Order Confirmation – ${o.id} | Sedra Home`;
    const body    =
`Dear ${r.name},

Thank you for your order with Sedra Home! 🌿

────────────────────────────────
Order Reference : ${o.id}
Date            : ${o.created_at?.slice(0, 10)}
────────────────────────────────

Items:
${items}

────────────────────────────────
Total: ${fmt(o.total)}${o.catalogue_type === 'vat' ? '\n(Prices include 14% VAT)' : ''}
────────────────────────────────

We will be in touch shortly to confirm delivery details.

Best regards,
Sedra Home Team
sedrahome.com`;
    window.open(`mailto:${r.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const sendWhatsApp = (o) => {
    const r = recipientFor(o);
    const rawPhone  = (r.phone || '').replace(/\D/g, '');
    if (!rawPhone) return alert('No phone number for this order.');
    const intlPhone = rawPhone.startsWith('20')
      ? rawPhone
      : rawPhone.startsWith('0') ? '20' + rawPhone.slice(1) : '20' + rawPhone;
    const items = (o.items || []).map((i) =>
      `• ${lang === 'ar' && i.name_ar ? i.name_ar : i.name_en} x${i.qty} — ${fmt(i.price * i.qty)}`
    ).join('\n');
    const msg =
`🌿 *Sedra Home — Order Confirmation*

Dear *${r.name}*,

Your order has been received! ✅

*Order ID:* ${o.id}
*Date:* ${o.created_at?.slice(0, 10)}

*Items:*
${items}

*Total: ${fmt(o.total)}*

We will contact you soon to arrange delivery.
Thank you for choosing Sedra Home! 🏡`;
    window.open(`https://wa.me/${intlPhone}?text=${encodeURIComponent(msg)}`);
  };

  // ── Cancel / Delete ───────────────────────────────────────────────────────
  const handleCancel = (id) => {
    const ar = lang === 'ar';
    if (!window.confirm(ar ? 'هل تريد إلغاء هذا الطلب؟' : 'Cancel this order?')) return;
    updateStatus({ orderId: id, newStatus: 'cancelled' });
  };

  const handleDelete = (id) => {
    const ar = lang === 'ar';
    if (!window.confirm(ar
      ? 'سيتم حذف هذا الطلب نهائياً. هل أنت متأكد؟'
      : 'Permanently delete this order? This cannot be undone.'
    )) return;
    deleteOrder({ orderId: id });
  };

  const handleSaveNote = (id) => {
    const note = (adminNoteEdit[id] ?? '').trim();
    saveNote(
      { orderId: id, note },
      {
        onSuccess: () =>
          setAdminNoteEdit((n) => { const c = { ...n }; delete c[id]; return c; }),
      }
    );
  };

  // ── Merge orders ─────────────────────────────────────────────────────────
  const handleMerge = async () => {
    const ids = Array.from(selectedForMerge);
    if (ids.length < 2) return;
    if (!window.confirm(lang === 'ar'
      ? `دمج ${ids.length} طلبات في طلب واحد؟ سيتم إلغاء الطلبات الأصلية.`
      : `Merge ${ids.length} orders into one? Original orders will be cancelled.`)) return;
    setMergeLoading(true); setMergeMsg('');
    try {
      const { data } = await supaFetch('POST', 'rpc/merge_orders', { p_order_ids: ids }, '', { 'Prefer': 'return=representation' });
      const newId = Array.isArray(data) ? data[0] : data;
      setMergeMsg(`OK:${lang === 'ar' ? 'تم الدمج:' : 'Merged:'} ${newId}`);
      setSelectedForMerge(new Set());
    } catch (e) {
      const msg = e.message?.includes('different_retailers')
        ? (lang === 'ar' ? 'الطلبات من عملاء مختلفين' : 'Orders belong to different retailers')
        : e.message;
      setMergeMsg('ERR:' + msg);
    }
    setMergeLoading(false);
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const inp = {
    padding: '8px 12px', borderRadius: 7, border: '1.5px solid #e0d9cf',
    fontSize: 13, outline: 'none', background: '#fff',
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Error state
  if (isError) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--status-danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <Icon name="TriangleAlert" size={16} /> {lang === 'ar' ? 'تعذّر تحميل الطلبات. يرجى التحديث.' : 'Failed to load orders. Please refresh.'}
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, color: 'var(--sedra-forest-soft)' }}>
          {t.orders}
          {isFetching && !isLoading && (
            <span style={{ marginLeft: 8, fontSize: 12, color: '#aaa', fontWeight: 400 }}>↻</span>
          )}
        </h2>
        {canSeeTotals && (
          <button onClick={exportCSV} style={btnStyle()}>{t.exportCSV}</button>
        )}
      </div>

      {/* ── Search + Filter bar ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder={lang === 'ar' ? 'بحث في الطلبات...' : 'Search orders…'}
          value={orderSearch}
          onChange={(e) => setOrderSearch(e.target.value)}
          style={{ ...inp, flex: 1, minWidth: 180 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          style={{ ...inp }}
        >
          <option value='all'>{lang === 'ar' ? 'كل الحالات' : 'All statuses'}</option>
          {['new', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map((s) => (
            <option key={s} value={s}>
              {lang === 'ar' ? (STATUS_LABELS[s] || {}).ar : (STATUS_LABELS[s] || {}).en}
            </option>
          ))}
        </select>
        <input type='date' value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...inp }} title='From date' />
        <input type='date' value={dateTo}   onChange={(e) => setDateTo(e.target.value)}   style={{ ...inp }} title='To date' />
        {(orderSearch || statusFilter !== 'all' || dateFrom || dateTo) && (
          <button
            onClick={() => { setOrderSearch(''); handleStatusChange('all'); setDateFrom(''); setDateTo(''); }}
            style={{ ...btnStyle('#888'), fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Icon name="X" size={12} /> {lang === 'ar' ? 'مسح' : 'Clear'}
          </button>
        )}
        <span style={{ fontSize: 12, color: '#aaa', whiteSpace: 'nowrap' }}>
          {filteredOrders.length} / {totalCount}
        </span>
      </div>

      {/* ── Merge bar ── */}
      {mergeMsg && (
        <div style={{ background: mergeMsg.startsWith('OK:') ? '#f0fdf4' : '#fff5f5', border: '1px solid ' + (mergeMsg.startsWith('OK:') ? '#bbf7d0' : '#fecaca'), borderRadius: 8, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: mergeMsg.startsWith('OK:') ? '#166534' : '#b91c1c', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name={mergeMsg.startsWith('OK:') ? 'CheckCircle2' : 'XCircle'} size={14} /> {mergeMsg.replace(/^(OK:|ERR:)/, '')}
          </span>
          <button onClick={() => setMergeMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex' }}><Icon name="X" size={16} /></button>
        </div>
      )}
      {selectedForMerge.size > 0 && (
        <div style={{ background: 'var(--sedra-forest)', borderRadius: 9, padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: '#E2EDD8', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="ArrowLeftRight" size={14} /> {selectedForMerge.size} {lang === 'ar' ? 'طلبات محددة للدمج' : 'orders selected for merge'}
          </span>
          <button onClick={handleMerge} disabled={mergeLoading || selectedForMerge.size < 2}
            style={{ padding: '7px 18px', borderRadius: 7, background: mergeLoading ? '#666' : 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            {mergeLoading ? '…' : (lang === 'ar' ? 'دمج الطلبات' : 'Merge Orders')}
          </button>
          <button onClick={() => setSelectedForMerge(new Set())}
            style={{ padding: '7px 12px', borderRadius: 7, background: 'transparent', color: '#aaa', border: '1px solid var(--sedra-forest-soft)', cursor: 'pointer', fontSize: 12 }}>
            {lang === 'ar' ? 'إلغاء' : 'Clear'}
          </button>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 12, height: 100, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', opacity: 0.5 }} />
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>
          {orders.length === 0
            ? t.noOrders
            : lang === 'ar' ? 'لا توجد نتائج' : 'No orders match the filter'}
        </div>
      ) : filteredOrders.map((o) => {
        const effStatus   = ['new', 'seen'].includes(o.status) ? 'new' : o.status;
        const sl          = STATUS_LABELS[effStatus] || STATUS_LABELS.new;
        const isCancelled = o.status === 'cancelled';
        return (
          <div key={o.id} style={{
            background: '#fff', borderRadius: 12,
            padding: isMobile ? '14px 16px' : '18px 22px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
            marginBottom: 14,
            borderLeft: `4px solid ${sl.color}`,
            opacity: isCancelled ? 0.72 : 1,
          }}>

            {/* ── Top row ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* Merge checkbox — only for non-cancelled, super admin */}
                {isSuperAdmin && !isCancelled && (
                  <input type="checkbox"
                    checked={selectedForMerge.has(o.id)}
                    onChange={() => setSelectedForMerge(prev => {
                      const next = new Set(prev);
                      next.has(o.id) ? next.delete(o.id) : next.add(o.id);
                      return next;
                    })}
                    title={lang === 'ar' ? 'تحديد للدمج' : 'Select for merge'}
                    style={{ width: 16, height: 16, accentColor: 'var(--sedra-forest-soft)', cursor: 'pointer', flexShrink: 0 }} />
                )}
                <span style={{ fontWeight: 800, color: 'var(--sedra-forest)', fontSize: 15 }}>{o.business_name || o.contact_name}</span>
                {o.business_name && o.contact_name && o.business_name !== o.contact_name && (
                  <span style={{ fontSize: 12, color: '#888' }}>({o.contact_name})</span>
                )}
                <span style={{ fontSize: 12, color: '#aaa' }}>{o.created_at?.slice(0, 10)}</span>
                <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 10, fontWeight: 700, background: sl.bg, color: sl.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name={sl.icon} size={11} /> {lang === 'ar' ? sl.ar : sl.en}
                </span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'var(--sedra-sage-wash)', color: 'var(--sedra-sage-ink)', fontWeight: 600 }}>
                  {o.catalogue_type}
                </span>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {!isCancelled && canStatus && (() => {
                  const pipelineStatus = o.status === 'seen' ? 'confirmed' : o.status;
                  const nextStage      = STATUS_STAGES[STATUS_STAGES.indexOf(pipelineStatus) + 1];
                  if (!nextStage) return null;
                  const nl = STATUS_LABELS[nextStage] || {};
                  return (
                    <button onClick={() => updateStatus({ orderId: o.id, newStatus: nextStage })} style={{ ...btnStyle(nl.color || '#9ca3af'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Icon name="ArrowRight" size={12} /> {lang === 'ar' ? nl.ar : nl.en}
                    </button>
                  );
                })()}
                {canSeeTotals && !isCancelled && (
                  <button onClick={() => exportInvoice(o)} style={{ ...btnStyle('var(--sedra-olive)'), display: 'inline-flex', alignItems: 'center', gap: 5 }} disabled={xlsxLoading === o.id}>
                    <Icon name={xlsxLoading === o.id ? 'Loader2' : 'ChartColumn'} size={13} /> {t.exportInvoice}
                  </button>
                )}
                {o.email && !isCancelled && (
                  <button onClick={() => sendEmail(o)} style={{ ...btnStyle('#3b82f6'), display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="Mail" size={13} /> {t.sendEmail}</button>
                )}
                {!isCancelled && (
                  <button onClick={() => sendWhatsApp(o)} style={{ ...btnStyle('#25d366'), display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="MessageCircle" size={13} /> {t.sendWhatsApp}</button>
                )}
                <button onClick={() => printOrder(o)} disabled={!!sheetLoading}
                  style={{ ...btnStyle('#6b7280'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Icon name="Printer" size={13} /> {lang === 'ar' ? 'طباعة' : 'Print'}
                </button>
                {/* Same invoice with product photos, compressed so the PDF
                    stays small enough to email or send on WhatsApp. */}
                <button onClick={() => printOrder(o, { withPictures: true })} disabled={!!sheetLoading}
                  title={lang === 'ar' ? 'فاتورة PDF مع صور المنتجات (حجم مضغوط)' : 'PDF invoice with product photos (compressed)'}
                  style={{ ...btnStyle('var(--sedra-forest)'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Icon name={sheetLoading === o.id + ':pic' ? 'Loader2' : 'Image'} size={13} />
                  {sheetLoading === o.id + ':pic'
                    ? (lang === 'ar' ? 'جارٍ التجهيز…' : 'Preparing…')
                    : (lang === 'ar' ? 'فاتورة بالصور' : 'Invoice + pictures')}
                </button>
                {isSuperAdmin && !isCancelled && (
                  <button onClick={() => handleCancel(o.id)} style={{ ...btnStyle('#f59e0b'), display: 'inline-flex', alignItems: 'center', gap: 5 }} title={lang === 'ar' ? 'إلغاء الطلب' : 'Cancel order'}>
                    <Icon name="Ban" size={13} /> {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                  </button>
                )}
                {isSuperAdmin && (
                  <button onClick={() => handleDelete(o.id)} style={{ ...btnStyle('var(--status-danger)'), display: 'inline-flex', alignItems: 'center', gap: 5 }} title={lang === 'ar' ? 'حذف نهائي' : 'Delete permanently'}>
                    <Icon name="Trash2" size={13} /> {lang === 'ar' ? 'حذف' : 'Delete'}
                  </button>
                )}
              </div>
            </div>

            {/* ── Status pipeline ── */}
            {canStatus && (
              <div style={{ marginBottom: 10, padding: '8px 0 2px' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
                  {STATUS_STAGES.map((stage, i) => {
                    const stageLbl    = STATUS_LABELS[stage] || {};
                    const effSt       = o.status === 'seen' ? 'confirmed' : o.status;
                    const currentIdx  = STATUS_STAGES.indexOf(effSt);
                    const isDone      = i <= currentIdx;
                    const isCurrent   = effSt === stage;
                    return (
                      <React.Fragment key={stage}>
                        {i > 0 && <div style={{ flex: 1, minWidth: 8, height: 2, background: isDone ? 'var(--sedra-sage-ink)' : '#e5e7eb' }} />}
                        <button
                          onClick={() => updateStatus({ orderId: o.id, newStatus: stage })}
                          title={'Set to: ' + (lang === 'ar' ? stageLbl.ar : stageLbl.en)}
                          style={{
                            padding: '3px 8px', borderRadius: 16, border: '2px solid',
                            borderColor: isCurrent ? stageLbl.color : isDone ? 'var(--sedra-sage-ink)' : '#e5e7eb',
                            background:  isCurrent ? stageLbl.bg    : isDone ? '#f0f9f0' : '#f9fafb',
                            color:       isCurrent ? stageLbl.color : isDone ? 'var(--sedra-forest-soft)' : '#9ca3af',
                            fontSize: 10, fontWeight: isCurrent ? 800 : 500,
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            boxShadow: isCurrent ? `0 0 0 2px ${stageLbl.color}44` : 'none',
                          }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name={stageLbl.icon} size={11} /> {lang === 'ar' ? stageLbl.ar : stageLbl.en}</span>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Contact info ── */}
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="Phone" size={12} /> {o.phone}</span>
              {o.whatsapp && o.whatsapp !== o.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="MessageCircle" size={12} /> {o.whatsapp}</span>}
              {o.payment_method && (
                <span style={{ padding: '2px 8px', borderRadius: 8, background: 'var(--sedra-sage-wash)', color: 'var(--sedra-sage-ink)', fontWeight: 600, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {PAYMENT_LABELS[o.payment_method]
                    ? (<><Icon name={PAYMENT_LABELS[o.payment_method].icon} size={12} /> {PAYMENT_LABELS[o.payment_method].label}</>)
                    : o.payment_method}
                </span>
              )}
              {o.email   && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="Mail" size={12} /> {o.email}</span>}
              {o.address && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="MapPin" size={12} /> {o.address}</span>}
              {o.placed_by_admin && (
                <span title={lang === 'ar' ? 'طلب مسجَّل بواسطة الفريق نيابة عن العميل' : 'Placed by staff on the customer’s behalf'}
                  style={{ padding: '2px 8px', borderRadius: 8, background: 'var(--status-info-bg)', color: 'var(--status-info)', fontWeight: 700, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="Eye" size={11} /> {lang === 'ar' ? 'بواسطة' : 'On behalf'} · {o.placed_by_admin}
                </span>
              )}
            </div>

            {/* ── Fulfillment contact ──────────────────────────────────────
                Who we contact about THIS order now. Defaults to the account's
                current primary; pin a specific person when an order needs to
                follow someone else. Never rewrites who placed the order. */}
            {o.retailer_id && contactsForAccount(o.retailer_id).length > 0 && (() => {
              const r = recipientFor(o);
              const options = contactsForAccount(o.retailer_id);
              return (
                <div style={{ background: 'var(--sedra-sage-wash)', borderRadius: 8, padding: '9px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sedra-forest)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="Users" size={12} /> {lang === 'ar' ? 'مسؤول التنفيذ:' : 'Fulfilment contact:'}
                  </span>
                  <select
                    value={o.fulfillment_contact_id || ''}
                    disabled={setFulfillment.isPending}
                    onChange={e => setFulfillment.mutate({ orderId: o.id, contactId: e.target.value || null })}
                    style={{ padding: '5px 9px', borderRadius: 6, border: '1.5px solid var(--sedra-line)', fontSize: 12.5, fontFamily: 'inherit', background: '#fff', outline: 'none', maxWidth: 260 }}>
                    <option value="">
                      {lang === 'ar' ? 'جهة الاتصال الأساسية للحساب' : 'Account primary contact'}
                      {r.source === 'primary' && r.contact ? ` — ${r.contact.name}` : ''}
                    </option>
                    {options.map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.job_title ? ` — ${c.job_title}` : ''}</option>
                    ))}
                  </select>
                  {r.differsFromPlacedBy && (
                    <span style={{ fontSize: 11.5, color: 'var(--status-warn-dark)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Icon name="TriangleAlert" size={11} />
                      {lang === 'ar'
                        ? `المراسلات تذهب إلى ${r.name} (الطلب سجّله ${o.contact_name})`
                        : `Comms go to ${r.name} — order was placed by ${o.contact_name}`}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* ── Items table ── */}
            {Array.isArray(o.items) && o.items.length > 0 && (
              <div style={{ background: '#fafaf8', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--sedra-line)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--sedra-sage-wash)' }}>
                      <th style={{ padding: '7px 12px', textAlign: 'left',  color: '#888', fontWeight: 600 }}>SKU</th>
                      <th style={{ padding: '7px 12px', textAlign: 'left',  color: '#888', fontWeight: 600 }}>Product</th>
                      <th style={{ padding: '7px 12px', textAlign: 'center',color: '#888', fontWeight: 600 }}>Qty</th>
                      {canSeeTotals && <th style={{ padding: '7px 12px', textAlign: 'right', color: '#888', fontWeight: 600 }}>Subtotal</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((item, j) => (
                      <tr key={item.sku || item.id || j} style={{ borderTop: '1px solid var(--sedra-line)', background: j % 2 === 0 ? '#fff' : '#fafaf8' }}>
                        <td style={{ padding: '7px 12px', color: '#aaa', fontSize: 12 }}>{item.sku}</td>
                        <td style={{ padding: '7px 12px', fontWeight: 500, color: 'var(--sedra-forest)' }}>
                          {lang === 'ar' && item.name_ar ? item.name_ar : item.name_en}
                        </td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', fontWeight: 700 }}>×{item.qty}</td>
                        {canSeeTotals && (
                          <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--sedra-forest-soft)' }}>
                            {fmt(item.price * item.qty)}
                          </td>
                        )}
                      </tr>
                    ))}
                    {canSeeTotals && (
                      <tr style={{ borderTop: '2px solid #e8e3da' }}>
                        <td colSpan={3} style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 800, color: 'var(--sedra-forest)', fontSize: 14 }}>
                          {o.catalogue_type === 'vat' ? 'Total (incl. 14% VAT)' : 'Total'}
                        </td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 900, color: 'var(--sedra-forest-soft)', fontSize: 15 }}>
                          {fmt(o.total)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {o.notes && (
              <div style={{ marginTop: 10, fontSize: 13, color: '#666', fontStyle: 'italic', padding: '8px 12px', background: '#fffbf5', borderRadius: 6, borderLeft: '3px solid #e8d9b8', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <Icon name="FileText" size={13} style={{ flexShrink: 0, marginTop: 2 }} /> {lang === 'ar' ? 'ملاحظة العميل: ' : 'Customer note: '}{o.notes}
              </div>
            )}

            {/* ── Admin notes ── */}
            <div style={{ marginTop: 8 }}>
              {adminNoteEdit[o.id] !== undefined ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <textarea
                    autoFocus
                    value={adminNoteEdit[o.id]}
                    onChange={(e) => setAdminNoteEdit((n) => ({ ...n, [o.id]: e.target.value }))}
                    placeholder={lang === 'ar' ? 'ملاحظة داخلية...' : 'Internal admin note…'}
                    rows={2}
                    style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: '1.5px solid var(--sedra-sage-ink)', fontSize: 12, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
                  />
                  <button
                    onClick={() => handleSaveNote(o.id)}
                    disabled={savingNote}
                    style={{ ...btnStyle('var(--sedra-olive)'), padding: '7px 12px', fontSize: 11 }}
                  >
                    {savingNote ? '…' : lang === 'ar' ? 'حفظ' : 'Save'}
                  </button>
                  <button
                    onClick={() => setAdminNoteEdit((n) => { const c = { ...n }; delete c[o.id]; return c; })}
                    style={{ ...btnStyle('#888'), padding: '7px 10px', display: 'flex' }}
                  ><Icon name="X" size={13} /></button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {o.admin_notes && (
                    <div style={{ flex: 1, fontSize: 12, color: '#5a6e50', background: '#eef4ea', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid var(--sedra-sage-ink)', fontStyle: 'italic', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <Icon name="Lock" size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {lang === 'ar' ? 'ملاحظة داخلية: ' : 'Admin note: '}{o.admin_notes}
                    </div>
                  )}
                  <button
                    onClick={() => setAdminNoteEdit((n) => ({ ...n, [o.id]: o.admin_notes || '' }))}
                    style={{ background: 'none', border: '1px dashed #aaa', color: '#aaa', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
                  >
                    {o.admin_notes ? <Icon name="Pencil" size={12} /> : (lang === 'ar' ? '+ ملاحظة داخلية' : '+ Admin note')}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Pagination controls ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 24, flexWrap: 'wrap' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ ...btnStyle(page === 1 ? '#d1d5db' : 'var(--sedra-olive)'), cursor: page === 1 ? 'default' : 'pointer' }}
          >
            ← {lang === 'ar' ? 'السابق' : 'Prev'}
          </button>

          {/* Page number buttons — show at most 7 around the current page */}
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
            .reduce((acc, n, i, arr) => {
              if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
              acc.push(n);
              return acc;
            }, [])
            .map((item, i) =>
              item === '…'
                ? <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#aaa' }}>…</span>
                : <button
                    key={item}
                    onClick={() => setPage(item)}
                    style={{
                      ...btnStyle(item === page ? 'var(--sedra-olive)' : 'var(--sedra-sage-wash)'),
                      color: item === page ? '#fff' : 'var(--sedra-forest-soft)',
                      minWidth: 34,
                    }}
                  >
                    {item}
                  </button>
            )
          }

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ ...btnStyle(page === totalPages ? '#d1d5db' : 'var(--sedra-olive)'), cursor: page === totalPages ? 'default' : 'pointer' }}
          >
            {lang === 'ar' ? 'التالي' : 'Next'} →
          </button>

          <span style={{ fontSize: 12, color: '#aaa', marginLeft: 8 }}>
            {lang === 'ar'
              ? `صفحة ${page} من ${totalPages} (${totalCount} طلب)`
              : `Page ${page} of ${totalPages} (${totalCount} orders)`}
          </span>
        </div>
      )}
    </div>
  );
}
